import { Users } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';
import { formatMoney, timeAgo } from '@/src/lib/format';
import type { OwnerMetrics } from '@/src/hooks/useOwnerMetrics';

export default function StaffingWidget({ data, loading }: { data: OwnerMetrics | null; loading: boolean }) {
  const c = useAppColors();
  const d = data?.deputy;
  const maxHours = Math.max(1, ...(d?.byDay ?? []).map((b) => b.hours));
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-2xl border p-4 h-full bg-warm-gray flex flex-col" style={{ borderColor: c.border }}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: c.accentBg }}>
          <Users size={15} style={{ color: c.accentSoft }} />
        </div>
        <span className="text-[9px] font-bold tracking-widest uppercase" style={{ color: c.textFaint }}>Staffing · Deputy</span>
      </div>

      {loading && !data ? (
        <p className="text-xs" style={{ color: c.textFaint }}>Loading…</p>
      ) : !d?.connected ? (
        <p className="text-xs leading-relaxed" style={{ color: c.textDim }}>
          Connect Deputy in <strong>Integrations</strong> to see who's on and labour hours here.
        </p>
      ) : d.error ? (
        <p className="text-xs" style={{ color: '#DC2626' }}>{d.error}</p>
      ) : (
        <>
          <p className="text-[11px]" style={{ color: c.textFaint }}>On today</p>
          <p className="text-2xl font-semibold leading-tight" style={{ color: c.text }}>
            {d.staffToday ?? 0} <span className="text-sm font-normal" style={{ color: c.textDim }}>people · {d.shiftsToday ?? 0} shifts</span>
          </p>
          <div className="flex gap-4 mt-2 text-xs">
            <span style={{ color: c.textDim }}>Hours <strong style={{ color: c.text }}>{d.hours ?? 0}h</strong></span>
            <span style={{ color: c.textDim }}>
              Cost <strong style={{ color: c.text }}>{d.cost != null ? formatMoney(d.cost) : '—'}</strong>
            </span>
          </div>
          {d.cost == null && (d.hours ?? 0) > 0 && (
            <p className="text-[11px] mt-1" style={{ color: c.textFaint }}>Add pay rates in Deputy to see labour cost.</p>
          )}

          {/* This week's hours */}
          <div className="mt-auto pt-3 flex items-end gap-1.5 h-16">
            {(d.byDay ?? []).map((b) => (
              <div key={b.date} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-sm"
                  style={{
                    height: `${Math.max(3, (b.hours / maxHours) * 40)}px`,
                    background: b.date === todayIso ? c.accent : c.accentBorder,
                  }}
                  title={`${b.day}: ${b.hours}h`}
                />
                <span className="text-[9px]" style={{ color: c.textFaint }}>{b.day.slice(0, 1)}</span>
              </div>
            ))}
          </div>
          {d.lastSynced && (
            <p className="text-[10px] mt-2" style={{ color: c.textFaint }}>Roster synced {timeAgo(d.lastSynced)}</p>
          )}
        </>
      )}
    </div>
  );
}